import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { CommentItem, NormalizedComment } from '../../components/common/CommentItem';
import { PhotoImage } from './PhotoImage';
import { useAppSelector } from '../../store';
import { apiVariables } from '../../services/api/apiVariables';
import { getToken } from '../../utils/tokenUtils';

interface CommentModalProps {
  isOpen: boolean;
  postId?: number;
  currentUserName?: string;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

function getInitials(name?: string): string {
  if (!name) return 'U';

  const trimmed = name.trim();

  if (!trimmed) return 'U';

  // Special case for "You"
  if (trimmed.toLowerCase() === 'you') {
    return 'You';
  }

  const words = trimmed.split(/\s+/).filter(Boolean);

  if (words.length === 0) return 'U';

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return (
    words[0][0] + (words.at(-1)?.[0] || '')
  ).toUpperCase();
}

function getValidImageId(value?: number | string | null): number | null {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function getSafeUserName(comment: any) {
  const user = comment.user || {};
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const nameParts = [firstName, lastName].filter(Boolean);
  const name = nameParts.join(' ').trim();
  return (
    user.name ||
    name ||
    comment.userName ||
    comment.commentedByUserName ||
    comment.fullName ||
    'User'
  );
}

function normalizeComment(raw: any): NormalizedComment {
  const user = raw.user || {};
  return {
    commentId: raw.commentId ?? raw.id ?? raw.photoCommentId ?? Math.random().toString(36).slice(-8),
    userName: getSafeUserName(raw),
    specialtyName: user.specialtyName || raw.specialtyName || '',
    commentText: raw.commentText || raw.comment || raw.text || '',
    createdAt: raw.createdAt || raw.updatedAt || new Date().toISOString(),
    profilePhotoId: getValidImageId(user.profilePhotoId ?? raw.profilePhotoId ?? raw.profileImageId),
  };
}

export default function CommentModal({
  isOpen,
  postId,
  currentUserName,
  onClose,
  value,
  onChange,
  onSubmit,
  isSubmitting,
}: Readonly<CommentModalProps>) {
  const { theme } = useTheme();
  const currentUserPhotoId = useAppSelector((state) => state.userProfile.personalInfo?.photoId);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [fetchedComments, setFetchedComments] = useState<any[] | null>(null);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const normalizedComments = useMemo(
    () => (fetchedComments ?? []).map(normalizeComment),
    [fetchedComments]
  );

  useEffect(() => {
    if (!isOpen) {
      setFetchedComments(null);
      setIsLoadingComments(false);
      return;
    }

    if (!postId || isSubmitting) return;

    let mounted = true;
    const controller = new AbortController();
    setFetchedComments(null);
    setIsLoadingComments(true);

    const getAllComments = async () => {
      try {
        const response = await fetch(`${apiVariables.POST_GET_BY_ID}posts/${postId}/comments`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          console.error('Failed to load comments', response.statusText);
          if (mounted) setFetchedComments([]);
          return;
        }

        const data = await response.json();
        const allComments = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (mounted) setFetchedComments(allComments);
      } catch (error) {
        if (mounted && (error as Error).name !== 'AbortError') {
          console.error('Error loading comments:', error);
          setFetchedComments([]);
        }
      } finally {
        if (mounted) setIsLoadingComments(false);
      }
    };

    getAllComments();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [isOpen, postId, isSubmitting]);

  const handleInput = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const target = event.currentTarget;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 240)}px`;
    onChange(target.value);
  };

  useEffect(() => {
    if (!isOpen) return;
    const timer = globalThis.setTimeout(() => {
      textareaRef.current?.focus();
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 240)}px`;
      }
    }, 100);
    return () => globalThis.clearTimeout(timer);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center p-3 md:p-4">
      <button
        type="button"
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0,0,0,0.36)' }}
        onClick={onClose}
        aria-label="Close comments"
      />
      <div
        className="relative w-full md:max-w-3xl h-[70dvh] max-h-[70dvh] rounded-t-3xl md:rounded-[28px] shadow-2xl overflow-hidden flex flex-col"
        style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
      >
        <div
          className="flex items-center justify-between gap-3 px-5 py-4 border-b"
          style={{ borderColor: theme.border, backgroundColor: theme.surface }}
        >
          <div>
            <p className="text-sm font-medium" style={{ color: theme.textSecondary }}>
              Comments
            </p>
            <p className="text-lg font-semibold" style={{ color: theme.text }}>
              {normalizedComments.length} {normalizedComments.length === 1 ? 'Comment' : 'Comments'}
            </p>
          </div>
          <button
            type="button"
            className="p-2 rounded-full transition"
            style={{ color: theme.text }}
            onClick={onClose}
            aria-label="Close comments"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4 custom-scrollbar"
          style={{ backgroundColor: theme.background }}
        >
          {isLoadingComments || fetchedComments === null ? (
            <div className="flex h-full items-center justify-center text-sm" style={{ color: theme.textSecondary }}>
              Loading comments...
            </div>
          ) : normalizedComments.length === 0 ? (
            <div
              className="rounded-3xl border border-dashed p-6 text-center text-sm"
              style={{ borderColor: theme.border, backgroundColor: theme.surface, color: theme.textSecondary }}
            >
              No comments yet. Be the first to comment.
            </div>
          ) : (
            normalizedComments.map((comment) => (
              <CommentItem key={comment.commentId} comment={comment} />
            ))
          )}
        </div>

        <div
          className="sticky bottom-0 px-5 py-4"
          style={{ borderTop: `1px solid ${theme.border}`, backgroundColor: theme.surface }}
        >
          <div className="flex items-center gap-3 rounded-3xl border border-solid bg-white/80 p-4 shadow-sm"
            style={{ borderColor: theme.border, backgroundColor: theme.surfaceHover }}
          >
            <div className="flex-shrink-0 hidden sm:flex">
              <PhotoImage
                photoId={currentUserPhotoId}
                alt={currentUserName || 'You'}
                className="w-10 h-10 rounded-full object-cover"
                fallback={
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold"
                    style={{ backgroundColor: theme.primary, color: theme.contrastText }}
                  >
                    {getInitials(currentUserName)}
                  </div>
                }
              />
            </div>
            <div className="flex flex-1 items-center">
              <label htmlFor="comment-input" className="sr-only">
                Write a comment
              </label>
              <textarea
                id="comment-input"
                ref={textareaRef}
                rows={1}
                value={value}
                onInput={handleInput}
                placeholder="Write a thoughtful comment..."
                className="w-full rounded-2xl resize-none border-0 bg-transparent px-0 py-3 text-sm leading-6 outline-none focus:outline-none"
                style={{ color: theme.text }}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end sm:items-center">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm font-medium transition"
              style={{ color: theme.text }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: theme.primary, color: theme.contrastText }}
              onClick={onSubmit}
              disabled={isSubmitting || !value.trim()}
            >
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
